// Central content source for Innervation Energy Group.
// Designed to scale: pages read from these structures so new states,
// industries, and articles can be added without redesign.

export const COMPANY = {
  name: "Innervation Energy Group",
  short: "IEG",
  domain: "innervation.energy",
  email: "Info@innervation.energy",
  phone: "(800) 000-0000",
  addressLine: "United States",
  tagline: "Smarter Energy. Greater Value.",
  positioning:
    "A commercial energy advisory and procurement firm helping businesses make informed energy decisions across deregulated U.S. markets.",
  founded: "2019",
};

export const IMG = {
  hero: "https://static.prod-images.emergentagent.com/jobs/869a8405-7214-43fc-a099-834b9bf3e2b2/images/0b3de696110dd4f7e6c818e759139fa1e23d37f4c330d1fcfcb626b8fe6fd40a.png",
  transmissionNight:
    "https://static.prod-images.emergentagent.com/jobs/869a8405-7214-43fc-a099-834b9bf3e2b2/images/3e389487c5750ebe64dd1e7441114a2cd7516b75e7a744e2c9db0f163da9d457.png",
  marketAbstract:
    "https://static.prod-images.emergentagent.com/jobs/869a8405-7214-43fc-a099-834b9bf3e2b2/images/35f028a5f32937f3ac10a9a85f583b5db573eb771af446f2bfc52eeb9dc6f67b.png",
  battery:
    "https://static.prod-images.emergentagent.com/jobs/869a8405-7214-43fc-a099-834b9bf3e2b2/images/9ad71ac70cfd96e55ace80bad77cf620f6390a0f032c5d3195041590127a02fe.png",
  gas: "https://static.prod-images.emergentagent.com/jobs/869a8405-7214-43fc-a099-834b9bf3e2b2/images/424167fa5128013a7665f97117aeb9eb3201a1d3703cb34cbcfc1feb5e2804f0.png",
  officeTower:
    "https://images.unsplash.com/photo-1639781895346-054825a58d4a?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600&ixlib=rb-4.1.0",
  glassFacade:
    "https://images.pexels.com/photos/19821207/pexels-photo-19821207.jpeg?auto=compress&cs=tinysrgb&w=1600",
  transmissionDay:
    "https://images.unsplash.com/photo-1721498273717-7a152d3ec13e?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600&ixlib=rb-4.1.0",
  manufacturing:
    "https://images.unsplash.com/photo-1716643863806-989dd76ae093?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600&ixlib=rb-4.1.0",
  manufacturing2:
    "https://images.pexels.com/photos/34718922/pexels-photo-34718922.jpeg?auto=compress&cs=tinysrgb&w=1600",
  hotel:
    "https://images.unsplash.com/photo-1723465308831-29da05e011f3?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600&ixlib=rb-4.1.0",
  hospital:
    "https://images.unsplash.com/photo-1586773860418-d37222d8fce3?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600&ixlib=rb-4.1.0",
  warehouse:
    "https://images.pexels.com/photos/4487363/pexels-photo-4487363.jpeg?auto=compress&cs=tinysrgb&w=1600",
  dataCenter:
    "https://images.pexels.com/photos/5203849/pexels-photo-5203849.jpeg?auto=compress&cs=tinysrgb&w=1600",
  professionals:
    "https://images.pexels.com/photos/6950031/pexels-photo-6950031.jpeg?auto=compress&cs=tinysrgb&w=1600",
  boardroom:
    "https://images.unsplash.com/photo-1573167507387-6b4b98cb7c13?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600&ixlib=rb-4.1.0",
  heroTower:
    "https://images.unsplash.com/photo-1768128834406-f5b1b962af9d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
};

export const NAV = [
  {
    label: "Solutions",
    path: "/solutions",
    children: [
      { label: "Commercial Electricity", path: "/solutions/commercial-electricity" },
      { label: "Commercial Natural Gas", path: "/solutions/commercial-natural-gas" },
      { label: "Virtual Power Plant Network", path: "/solutions/battery-hosting" },
    ],
  },
  { label: "Service Areas", path: "/service-areas" },
  { label: "Industries", path: "/industries" },
  { label: "Market Center", path: "/market-center" },
  { label: "Education Center", path: "/education" },
  { label: "Resources", path: "/resources" },
  { label: "About", path: "/about" },
];

export const STATS = [
  { value: "18", suffix: "+", label: "Deregulated markets served" },
  { value: "40", suffix: "M+", label: "Annual kWh advised" },
  { value: "$120", suffix: "M", label: "Contract value managed" },
  { value: "100", suffix: "%", label: "Transparent, conflict-free advice" },
];

export const SERVICES = [
  {
    slug: "commercial-electricity",
    title: "Commercial Electricity",
    icon: "Zap",
    color: "#0052FF",
    image: IMG.transmissionDay,
    short:
      "Competitive electricity procurement, contract strategy, and price-risk management for commercial and industrial loads.",
    intro:
      "We help businesses navigate deregulated electricity markets with clarity — comparing suppliers, structuring contracts, and timing the market to protect your budget.",
    overview:
      "Electricity is one of the largest controllable line items on a commercial operating statement. In deregulated markets, you can choose your supplier and contract structure — but the landscape is complex and fast-moving. Innervation Energy Group acts as your independent advisor, bringing wholesale-level market intelligence and a disciplined procurement process to every decision.",
    benefits: [
      "Independent, conflict-free supplier comparisons across the market",
      "Fixed, index, and blended contract structures matched to your risk profile",
      "Budget certainty and defensible pricing for finance and procurement teams",
      "Ongoing market monitoring so you buy at the right time, not just on renewal",
    ],
    features: [
      { title: "Load & usage analysis", desc: "We analyze interval data and demand profiles to understand exactly how you consume energy." },
      { title: "Competitive RFP", desc: "We take your load to a curated network of vetted suppliers and negotiate on your behalf." },
      { title: "Contract structuring", desc: "Fixed, index, or hybrid — structured around your tolerance for price risk." },
      { title: "Ongoing management", desc: "We track expirations, market movements, and renewal windows so nothing lapses." },
    ],
    process: [
      { step: "Assess", desc: "Gather utility data, review current contracts, define objectives." },
      { step: "Strategy", desc: "Model scenarios and recommend a procurement approach." },
      { step: "Procure", desc: "Run a competitive process and secure optimized pricing." },
      { step: "Manage", desc: "Monitor markets and manage renewals continuously." },
    ],
  },
  {
    slug: "commercial-natural-gas",
    title: "Commercial Natural Gas",
    icon: "Flame",
    color: "#00C853",
    image: IMG.gas,
    short:
      "Strategic natural gas supply procurement and hedging for facilities, manufacturers, and multi-site operators.",
    intro:
      "Natural gas markets are volatile and seasonal. We bring procurement discipline and hedging strategy to help you manage cost and supply risk with confidence.",
    overview:
      "For facilities that rely on natural gas for heating, process load, or on-site generation, price volatility is a real operational risk. Innervation Energy Group helps commercial organizations procure natural gas strategically — evaluating supply options, structuring hedges, and aligning contract terms with seasonal usage patterns.",
    benefits: [
      "Supply procurement across competitive natural gas markets",
      "Seasonal and layered hedging strategies to smooth volatility",
      "Transportation and basis considerations built into every recommendation",
      "Consolidated management for multi-site and multi-utility portfolios",
    ],
    features: [
      { title: "Usage & seasonality", desc: "We model your consumption curve to plan around peak heating demand." },
      { title: "Supply RFP", desc: "Competitive sourcing across vetted natural gas suppliers." },
      { title: "Hedging strategy", desc: "Layered and fixed structures to manage exposure to price swings." },
      { title: "Portfolio management", desc: "Unified oversight across facilities, utilities, and contract terms." },
    ],
    process: [
      { step: "Assess", desc: "Review consumption, transportation, and current supply terms." },
      { step: "Strategy", desc: "Design a hedging and procurement plan for your risk profile." },
      { step: "Procure", desc: "Competitively secure supply and lock in favorable terms." },
      { step: "Manage", desc: "Monitor seasonal markets and adjust the strategy over time." },
    ],
  },
  {
    slug: "battery-hosting",
    title: "Virtual Power Plant Network",
    icon: "BatteryCharging",
    color: "#0052FF",
    image: IMG.battery,
    short:
      "Host battery energy storage on your commercial property and take part in Innervation Energy Group's growing Virtual Power Plant Network.",
    intro:
      "Innervation Energy Group is building a distributed energy infrastructure platform. We partner with commercial property owners to host battery energy storage systems, assembling a growing Virtual Power Plant Network designed to strengthen grid reliability and accelerate the transition to a smarter electric grid.",
    overview:
      "Battery hosting is how commercial property owners participate in our growing Virtual Power Plant Network. Rather than a single facility, a Virtual Power Plant is a coordinated fleet of distributed battery energy storage systems installed across many commercial sites. Innervation Energy Group partners with property owners to host these systems, turning underutilized land and electrical infrastructure into productive energy assets. As the network expands, it is designed to support grid reliability, improve energy resilience, and create opportunities to participate in future grid service and energy market programs where they become available. We are an energy infrastructure company building tomorrow's distributed energy network, one commercial property at a time.",
    availabilityNote: "We are actively partnering with commercial property owners to host battery energy storage systems, with initial site evaluations focused on Texas and Illinois.",
    benefits: [
      "Generate value from underutilized land and electrical infrastructure",
      "Participate in the future of distributed energy",
      "Increase property value through energy infrastructure",
      "Support a cleaner, more resilient electric grid",
      "Create new long-term revenue opportunities where market programs exist",
    ],
    features: [
      { title: "Site screening", desc: "We assess your property's land, electrical infrastructure, and proximity to the grid to gauge its fit as a host site." },
      { title: "Network partnership", desc: "Qualified sites join our growing Virtual Power Plant Network as host locations for battery energy storage systems." },
      { title: "Infrastructure development", desc: "We coordinate with vetted storage developers to design, install, and operate the systems on your property." },
      { title: "Long-term value", desc: "Hosting can create new revenue and add energy infrastructure value while supporting a more resilient grid." },
    ],
    process: [
      { step: "Screen", desc: "Share your property details so we can evaluate its hosting potential." },
      { step: "Evaluate", desc: "We assess grid proximity, electrical infrastructure, and site suitability." },
      { step: "Partner", desc: "Qualified sites join the Virtual Power Plant Network with clear, transparent terms." },
      { step: "Host", desc: "Battery systems are installed and operated, and we stay engaged as your advisor." },
    ],
  },
];

export const WHY_IEG = [
  {
    n: "01",
    title: "Commercial Energy Expertise",
    desc: "We focus exclusively on commercial and industrial energy — not residential. Every recommendation is grounded in deep, sector-specific knowledge of how businesses buy and use energy.",
  },
  {
    n: "02",
    title: "Strategic Procurement Guidance",
    desc: "We treat procurement as a strategy, not a transaction. Contract structure, timing, and risk posture are aligned to your financial objectives — not a supplier's incentives.",
  },
  {
    n: "03",
    title: "Industry Knowledge",
    desc: "Manufacturing, hospitality, healthcare, and multi-site operators all consume energy differently. We bring pattern recognition from across sectors to your specific situation.",
  },
  {
    n: "04",
    title: "Transparent Education",
    desc: "We believe informed clients make better decisions. Our Education Center and market insights exist to demystify commercial energy — no jargon, no pressure.",
  },
  {
    n: "05",
    title: "National Market Coverage",
    desc: "We operate across eligible deregulated markets nationwide, giving multi-site organizations a single, consistent advisor for their entire energy footprint.",
  },
];

export const INDUSTRIES = [
  {
    slug: "commercial-real-estate",
    name: "Commercial Real Estate",
    image: IMG.officeTower,
    summary:
      "Office towers and multi-tenant portfolios where energy cost directly impacts NOI and tenant satisfaction.",
    points: [
      "Portfolio-wide procurement across multiple buildings and meters",
      "Budget certainty that supports underwriting and NOI targets",
      "Green and renewable supply options to meet ESG commitments",
    ],
  },
  {
    slug: "manufacturing",
    name: "Manufacturing",
    image: IMG.manufacturing,
    summary:
      "Energy-intensive production facilities where electricity and gas are core inputs to cost of goods.",
    points: [
      "Demand analysis for high-load, continuous-process operations",
      "Hedging strategies to protect margins from price volatility",
      "Combined electricity and natural gas procurement strategy",
    ],
  },
  {
    slug: "hospitality",
    name: "Hospitality",
    image: IMG.hotel,
    summary:
      "Hotels and resorts with 24/7 operations and seasonal demand curves that reward smart contract timing.",
    points: [
      "Seasonality-aware procurement aligned to occupancy patterns",
      "Multi-property portfolio management for brands and operators",
      "Predictable energy budgets across the property portfolio",
    ],
  },
  {
    slug: "healthcare",
    name: "Healthcare",
    image: IMG.hospital,
    summary:
      "Hospitals and medical campuses with mission-critical, always-on energy requirements.",
    points: [
      "Reliability-first procurement for critical facilities",
      "Long-term budget stability for capital planning",
      "Resilience and backup-generation considerations",
    ],
  },
  {
    slug: "warehousing-logistics",
    name: "Warehousing & Logistics",
    image: IMG.warehouse,
    summary:
      "Distribution centers and cold storage with growing electrification and fleet-charging demand.",
    points: [
      "Scalable strategy for expanding EV and equipment charging load",
      "Multi-site consolidation across regional distribution networks",
      "Demand management for large, spiky electrical loads",
    ],
  },
  {
    slug: "data-centers",
    name: "Data Centers",
    image: IMG.dataCenter,
    summary:
      "Compute-intensive facilities where power is the single largest and most strategic operating cost.",
    points: [
      "High-volume electricity procurement and price-risk management",
      "Renewable and carbon-aware supply strategies",
      "Long-dated contract structures for capital-intensive projects",
    ],
  },
];

// Deregulated market coverage. availability keys: electricity, gas, battery
export const STATE_SERVICES = {
  tx: { electricity: true, gas: true, battery: true },
  il: { electricity: true, gas: true, battery: true },
  oh: { electricity: true, gas: true, battery: false },
  pa: { electricity: true, gas: true, battery: false },
  nj: { electricity: true, gas: true, battery: false },
  md: { electricity: true, gas: true, battery: false },
  ma: { electricity: true, gas: true, battery: false },
  ct: { electricity: true, gas: true, battery: false },
  ny: { electricity: true, gas: true, battery: false },
  me: { electricity: true, gas: false, battery: false },
  nh: { electricity: true, gas: false, battery: false },
  ri: { electricity: true, gas: true, battery: false },
  de: { electricity: true, gas: false, battery: false },
  dc: { electricity: true, gas: true, battery: false },
  mi: { electricity: true, gas: true, battery: false },
  va: { electricity: false, gas: true, battery: false },
  ga: { electricity: false, gas: true, battery: false },
  ca: { electricity: false, gas: true, battery: false },
  ky: { electricity: false, gas: true, battery: false },
  in: { electricity: false, gas: true, battery: false },
  wv: { electricity: false, gas: true, battery: false },
  fl: { electricity: false, gas: true, battery: false },
  co: { electricity: false, gas: true, battery: false },
};

export const STATE_NAMES = {
  al: "Alabama", ak: "Alaska", az: "Arizona", ar: "Arkansas", ca: "California",
  co: "Colorado", ct: "Connecticut", de: "Delaware", dc: "District of Columbia",
  fl: "Florida", ga: "Georgia", hi: "Hawaii", id: "Idaho", il: "Illinois",
  in: "Indiana", ia: "Iowa", ks: "Kansas", ky: "Kentucky", la: "Louisiana",
  me: "Maine", md: "Maryland", ma: "Massachusetts", mi: "Michigan", mn: "Minnesota",
  ms: "Mississippi", mo: "Missouri", mt: "Montana", ne: "Nebraska", nv: "Nevada",
  nh: "New Hampshire", nj: "New Jersey", nm: "New Mexico", ny: "New York",
  nc: "North Carolina", nd: "North Dakota", oh: "Ohio", ok: "Oklahoma",
  or: "Oregon", pa: "Pennsylvania", ri: "Rhode Island", sc: "South Carolina",
  sd: "South Dakota", tn: "Tennessee", tx: "Texas", ut: "Utah", vt: "Vermont",
  va: "Virginia", wa: "Washington", wv: "West Virginia", wi: "Wisconsin", wy: "Wyoming",
};

export const SERVICE_OPTIONS = [
  "Commercial Electricity",
  "Commercial Natural Gas",
  "Virtual Power Plant Network",
  "General Inquiry",
];

export const ARTICLE_CATEGORIES = [
  "Procurement",
  "Markets",
  "Regulation",
  "Sustainability",
  "Risk Management",
  "Battery Storage",
];

export const ARTICLES = [
  {
    slug: "what-is-energy-deregulation",
    category: "Regulation",
    title: "What Is Energy Deregulation and Why It Matters for Your Business",
    excerpt:
      "In deregulated markets, businesses can choose their energy supplier — creating both opportunity and complexity. Here's how it works.",
    readTime: "6 min",
    date: "2025-11-18",
    image: IMG.transmissionNight,
    featured: true,
    body: [
      { heading: "The utility vs. the supplier", body: "In a deregulated market, the local utility still owns the poles, wires, and pipes and delivers energy to your facility. What changes is that you can choose who supplies the actual electricity or natural gas. This separation of delivery and supply is the foundation of every competitive energy market." },
      { heading: "Why it creates opportunity", body: "Competition among suppliers means you can shop for pricing and contract structures that fit your business. But it also means the responsibility for making a smart decision shifts to you — and default rates are rarely the best available." },
      { heading: "The role of an advisor", body: "A commercial energy advisor helps you evaluate suppliers objectively, structure contracts around your risk profile, and time the market. The goal is not simply a lower rate today, but a defensible strategy over time." },
    ],
  },
  {
    slug: "fixed-vs-index-electricity-contracts",
    category: "Procurement",
    title: "Fixed vs. Index Electricity Contracts: Which Is Right for You?",
    excerpt:
      "Contract structure is one of the most consequential procurement decisions. Understand the trade-offs before you sign.",
    readTime: "7 min",
    date: "2025-11-05",
    image: IMG.officeTower,
    body: [
      { heading: "Fixed-rate contracts", body: "A fixed rate locks your price per unit for the length of the term, delivering budget certainty. It's ideal for organizations that prioritize predictability and want to remove market risk from their planning." },
      { heading: "Index and variable structures", body: "Index contracts float with the wholesale market. They can capture savings when prices fall but expose you to volatility when they rise. They suit sophisticated buyers with appetite and processes to manage risk." },
      { heading: "Blended approaches", body: "Many commercial buyers use a hybrid — fixing a portion of their load while leaving some exposed to the market. This layered strategy balances certainty and opportunity." },
    ],
  },
  {
    slug: "understanding-demand-charges",
    category: "Procurement",
    title: "Understanding Demand Charges on Your Commercial Bill",
    excerpt:
      "Demand charges can quietly dominate a commercial electricity bill. Learn what drives them and how to manage them.",
    readTime: "5 min",
    date: "2025-10-22",
    image: IMG.manufacturing,
    body: [
      { heading: "Energy vs. demand", body: "Your bill has two main components: energy (total kWh consumed) and demand (your highest sustained draw, in kW). Demand charges reflect the capacity the grid must reserve for your peak — and they can be a large share of the total." },
      { heading: "What drives your peak", body: "A single spike — starting large equipment simultaneously, for example — can set your demand charge for the entire month. Understanding your load profile is the first step to controlling it." },
      { heading: "Management strategies", body: "Staggering equipment startup, load shifting, and on-site storage can all reduce demand charges. An advisor helps quantify the opportunity against your specific interval data." },
    ],
  },
  {
    slug: "natural-gas-hedging-basics",
    category: "Risk Management",
    title: "Natural Gas Hedging Basics for Commercial Buyers",
    excerpt:
      "Natural gas prices are seasonal and volatile. A disciplined hedging strategy turns uncertainty into a manageable budget.",
    readTime: "8 min",
    date: "2025-10-09",
    image: IMG.gas,
    body: [
      { heading: "Why hedge?", body: "For facilities that depend on natural gas, price swings translate directly into margin risk. Hedging is about smoothing that volatility so your budget holds even when markets move." },
      { heading: "Layered purchasing", body: "Rather than buying all your supply at one moment, layered purchasing spreads procurement across time — reducing the risk of locking in at a market peak." },
      { heading: "Aligning to seasonality", body: "Because gas demand peaks in winter, timing purchases ahead of heating season and structuring around your consumption curve is essential to an effective strategy." },
    ],
  },
  {
    slug: "battery-storage-revenue-opportunity",
    category: "Battery Storage",
    title: "Inside the Virtual Power Plant Network: The Opportunity for Property Owners",
    excerpt:
      "Distributed battery storage is reshaping the grid. Here's how hosting a system connects your commercial property to a Virtual Power Plant Network.",
    readTime: "6 min",
    date: "2025-09-27",
    image: IMG.battery,
    body: [
      { heading: "Why storage, why now", body: "As grids add renewable generation, batteries provide the flexibility to store energy and release it when needed. Markets like ERCOT (Texas) and the Illinois grids are seeing rapid battery deployment." },
      { heading: "What makes a good site", body: "Developers look for acreage or rooftop space near substations and transmission with available interconnection capacity. Even modest commercial sites can qualify in the right location." },
      { heading: "How hosting works", body: "A landowner typically leases space to a storage developer, earning lease or partnership income over a long-term agreement — turning underutilized property into a new revenue stream." },
    ],
  },
  {
    slug: "renewable-energy-procurement-options",
    category: "Sustainability",
    title: "Renewable Energy Procurement Options for Commercial Buyers",
    excerpt:
      "From green supply to RECs and PPAs, businesses have more ways than ever to meet sustainability goals through procurement.",
    readTime: "7 min",
    date: "2025-09-12",
    image: IMG.glassFacade,
    body: [
      { heading: "Green supply products", body: "Many suppliers offer renewable-matched electricity products, allowing you to green your load without changing infrastructure — often at a modest premium." },
      { heading: "RECs explained", body: "Renewable Energy Certificates represent the environmental attributes of renewable generation. Purchasing RECs lets you credibly claim renewable usage against your consumption." },
      { heading: "Power Purchase Agreements", body: "For larger buyers, PPAs offer long-term contracts directly tied to renewable projects — supporting new generation while stabilizing long-term costs." },
    ],
  },
  {
    slug: "reading-your-interval-data",
    category: "Procurement",
    title: "How to Read Your Interval Data (and Why It Matters)",
    excerpt:
      "Interval data is the raw material of smart procurement. Understanding it unlocks better contracts and lower costs.",
    readTime: "5 min",
    date: "2025-08-30",
    image: IMG.dataCenter,
    body: [
      { heading: "What interval data is", body: "Most commercial meters record usage in 15-minute or hourly intervals. This granular picture reveals exactly when and how you consume energy across the day and year." },
      { heading: "What it reveals", body: "Load shape, peak timing, and seasonality all emerge from interval data. Suppliers price your risk based on this shape — a flat, predictable profile is cheaper to serve than a spiky one." },
      { heading: "Turning data into strategy", body: "By analyzing interval data, an advisor can recommend the right product, negotiate more effectively, and identify demand-management opportunities." },
    ],
  },
  {
    slug: "energy-budgeting-for-multi-site",
    category: "Procurement",
    title: "Energy Budgeting for Multi-Site Organizations",
    excerpt:
      "Managing energy across dozens of locations and utilities is complex. A unified strategy brings order and savings.",
    readTime: "6 min",
    date: "2025-08-14",
    image: IMG.warehouse,
    body: [
      { heading: "The multi-site challenge", body: "Operators with many locations face a patchwork of utilities, contract dates, and rate structures. Without coordination, contracts lapse to expensive default rates and savings opportunities are missed." },
      { heading: "Consolidation benefits", body: "A single advisor managing your entire portfolio brings consistency, aggregated buying power, and a unified renewal calendar — so nothing falls through the cracks." },
      { heading: "Reporting and control", body: "Portfolio-level reporting gives finance teams visibility into spend, exposure, and upcoming decisions across every site." },
    ],
  },
  {
    slug: "understanding-capacity-and-transmission-charges",
    category: "Markets",
    title: "Understanding Capacity and Transmission Charges",
    excerpt:
      "Beyond the energy rate, capacity and transmission charges shape your all-in cost. Here's what they are.",
    readTime: "6 min",
    date: "2025-07-29",
    image: IMG.transmissionDay,
    body: [
      { heading: "Capacity charges", body: "Capacity charges pay for the generation the grid must keep available to meet peak demand. Your contribution is often based on your usage during a handful of peak hours each year." },
      { heading: "Transmission charges", body: "Transmission charges cover the high-voltage network that moves power across regions. Like capacity, they are frequently tied to your demand during specific peak intervals." },
      { heading: "Managing peak-based charges", body: "Because these costs hinge on a few peak hours, curtailing load during those windows — peak shaving — can meaningfully reduce your all-in cost." },
    ],
  },
];

export const RESOURCES = [
  { title: "Commercial Energy Procurement Guide", type: "Guide", category: "Procurement", desc: "A step-by-step overview of how to run a disciplined procurement process for your organization." },
  { title: "Contract Renewal Checklist", type: "Checklist", category: "Procurement", desc: "Everything to review before your electricity or gas contract renews — so nothing lapses to default rates." },
  { title: "Deregulated Markets Reference Sheet", type: "Reference", category: "Markets", desc: "A quick-reference summary of where commercial electricity and gas choice is available." },
  { title: "Virtual Power Plant Network: Property Owner Brief", type: "Whitepaper", category: "Battery Storage", desc: "What commercial property owners should know about hosting battery energy storage as part of a distributed Virtual Power Plant Network." },
  { title: "Manufacturing Energy Strategy Case Study", type: "Case Study", category: "Manufacturing", desc: "How a multi-plant manufacturer stabilized energy costs through a layered hedging strategy." },
  { title: "Sustainability & Renewable Procurement Primer", type: "Guide", category: "Sustainability", desc: "Green supply, RECs, and PPAs explained for commercial sustainability leaders." },
];

export const MARKET_NEWS = [
  { tag: "Electricity", date: "2025-11-20", title: "Forward power curves ease heading into winter across PJM", excerpt: "Milder demand forecasts and strong gas storage have pressured forward electricity prices lower across the Mid-Atlantic." },
  { tag: "Natural Gas", date: "2025-11-12", title: "Storage levels remain above the five-year average", excerpt: "Robust production and a mild autumn have kept natural gas inventories comfortable ahead of the heating season." },
  { tag: "Battery Storage", date: "2025-10-30", title: "ERCOT battery capacity continues record expansion", excerpt: "Texas leads the nation in new grid-scale storage interconnections, expanding hosting opportunities for landowners." },
  { tag: "Regulation", date: "2025-10-18", title: "Capacity auction results reshape 2026 cost expectations", excerpt: "Recent capacity market outcomes are prompting commercial buyers to revisit contract timing and structure." },
  { tag: "Markets", date: "2025-10-02", title: "Volatility returns as weather models diverge", excerpt: "Uncertainty in long-range forecasts is widening the range of outcomes for winter energy pricing." },
];

export const MARKET_SERIES = {
  electricity: [
    { m: "Jan", v: 74 }, { m: "Feb", v: 71 }, { m: "Mar", v: 66 }, { m: "Apr", v: 61 },
    { m: "May", v: 58 }, { m: "Jun", v: 69 }, { m: "Jul", v: 82 }, { m: "Aug", v: 85 },
    { m: "Sep", v: 72 }, { m: "Oct", v: 64 }, { m: "Nov", v: 67 }, { m: "Dec", v: 76 },
  ],
  gas: [
    { m: "Jan", v: 3.9 }, { m: "Feb", v: 3.6 }, { m: "Mar", v: 3.1 }, { m: "Apr", v: 2.7 },
    { m: "May", v: 2.5 }, { m: "Jun", v: 2.8 }, { m: "Jul", v: 3.0 }, { m: "Aug", v: 3.2 },
    { m: "Sep", v: 3.1 }, { m: "Oct", v: 3.4 }, { m: "Nov", v: 3.8 }, { m: "Dec", v: 4.2 },
  ],
};

export const VALUES = [
  { title: "Independence", desc: "We work for you, not suppliers. Our advice is conflict-free by design." },
  { title: "Transparency", desc: "Clear reasoning, clear economics. You always understand the why behind a recommendation." },
  { title: "Rigor", desc: "Decisions are grounded in data, market intelligence, and disciplined process." },
  { title: "Partnership", desc: "We stay engaged well beyond the signature — as a long-term energy advisor." },
];

export const FAQS = [
  { q: "Is Innervation Energy Group an energy broker?", a: "We operate as an advisor first. While we run competitive procurement processes like a broker, our value is independent strategy, market intelligence, and education — aligned to your interests, not a single supplier's." },
  { q: "Which markets do you serve?", a: "We serve eligible deregulated markets across the United States for electricity and natural gas. Battery hosting for our Virtual Power Plant Network is currently evaluated in Texas and Illinois. See our Service Areas directory for details." },
  { q: "Do you work with multi-site organizations?", a: "Yes. Many of our clients operate across multiple states and utilities. We consolidate procurement and renewals into a single, coordinated strategy." },
  { q: "How do you get paid?", a: "Our engagement models are transparent and disclosed upfront. We'll walk you through exactly how we're compensated so there are no surprises." },
];
